import { API } from '../Api';

// Xử phạt người đặt đơn theo cấp (spec 2026-10-09-brand-orderer-sanctions). Route payment:
// - Người đặt đơn: api/v1/brand-sanctions/mine  -> /v1/payment-service/brand-sanctions/mine
// Không bao giờ có dấu vân tay CCCD hay tên admin trong DTO phía người đặt.

export type BrandSanctionLevel = 'Warning' | 'Suspend7' | 'Suspend30' | 'Permanent';

export interface SanctionDto {
  id: string;
  level: BrandSanctionLevel;
  reason: string;
  createdAt: string;
  /** null với cảnh cáo và khoá vĩnh viễn. */
  endsAt: string | null;
  revokedAt: string | null;
  /** Tổng VND bồi thường đã trừ (doanh nghiệp: ví VND; cá nhân: giá trị VND của các khoản). */
  compensatedVnd: number;
  /** Số Ca nạp đã trừ với người đặt cá nhân, null với doanh nghiệp. */
  compensatedCa: number | null;
}

export interface MySanctions {
  /** Lần phạt đang hiệu lực (khoá còn hạn hoặc vĩnh viễn), null nếu không có. */
  active: SanctionDto | null;
  history: SanctionDto[];
}

const ORDERER_BASE = '/v1/payment-service/brand-sanctions';

export const getMySanctions = () => API.get<MySanctions>(`${ORDERER_BASE}/mine`).then((r) => r.data);

// ─────────── Admin (Task 10) ───────────
// Route payment: api/v1/admin/brand-sanctions/* và api/v1/admin/brand-blacklist/* (quyền Ads.Manage, gỡ phạt và gỡ
// danh sách đen chỉ SuperAdmin). Thao tác ghi cần mã Authenticator: truyền `params: { totpCode }`, interceptor của API
// chuyển sang header X-Totp-Code (utils/admin-stepup.ts).

export type OrdererKindValue = 'Business' | 'Individual';
export type BrandCompensationSource = 'BusinessWallet' | 'TopUpCa';
export type BrandBlacklistKind = 'TaxCode' | 'IdentityFingerprint';

export interface AdminSanctionOrderer {
  adAccountId: string;
  ordererKind: OrdererKindValue;
  businessName: string;
  ownerIdentityName: string | null;
  taxCode: string | null;
  ownerUserId: string;
  ownerEmail: string | null;
  activeLevel: BrandSanctionLevel | null;
}

export interface AdminSanctionCompensation {
  creatorId: string;
  creatorName: string;
  amountVnd: number;
  netCa: number;
  sourceKind: BrandCompensationSource;
  sourceCa: number | null;
}

export interface AdminSanctionDto extends SanctionDto {
  adAccountId: string;
  ownerUserId: string;
  suggestedLevel: BrandSanctionLevel;
  lowerLevelReason: string | null;
  disputeId: string | null;
  createdBy: string;
  revokedBy: string | null;
  revokeReason: string | null;
  compensations: AdminSanctionCompensation[];
}

export interface SanctionPreviewCreator {
  creatorId: string;
  displayName: string;
  dealCount: number;
}

export interface SanctionPreview {
  adAccountId: string;
  ordererKind: OrdererKindValue;
  businessName: string;
  ownerIdentityName: string | null;
  countLast12Months: number;
  suggestedLevel: BrandSanctionLevel;
  active: AdminSanctionDto | null;
  history: AdminSanctionDto[];
  /** Ví VND khả dụng của doanh nghiệp (không tính hạn mức trả sau), null với cá nhân. */
  availableVnd: number | null;
  /** Ca nạp khả dụng của cá nhân, null với doanh nghiệp. */
  availableCa: number | null;
  vndPerCa: number;
  creators: SanctionPreviewCreator[];
}

export interface ApplySanctionPayload {
  adAccountId: string;
  level: BrandSanctionLevel;
  reason: string;
  lowerLevelReason?: string;
  disputeId?: string;
  compensations?: { creatorId: string; amountVnd: number; reason: string }[];
}

export interface BlacklistEntry {
  id: string;
  kind: BrandBlacklistKind;
  /** Mã số thuế đầy đủ; dấu vân tay chỉ 8 ký tự đầu kèm "…". */
  valueDisplay: string;
  sanctionId: string;
  businessName: string;
  sanctionReason: string;
  createdAt: string;
}

const ADMIN_SANCTION_BASE = '/v1/payment-service/admin/brand-sanctions';
const ADMIN_BLACKLIST_BASE = '/v1/payment-service/admin/brand-blacklist';

export const adminSearchOrderers = (q: string) =>
  API.get<AdminSanctionOrderer[]>(`${ADMIN_SANCTION_BASE}/orderers`, { params: { q } }).then((r) => r.data);

export const adminSanctionPreview = (adAccountId: string) =>
  API.get<SanctionPreview>(`${ADMIN_SANCTION_BASE}/preview`, { params: { adAccountId } }).then((r) => r.data);

export const adminApplySanction = (payload: ApplySanctionPayload, totpCode: string) =>
  API.post<AdminSanctionDto>(ADMIN_SANCTION_BASE, payload, { params: { totpCode } }).then((r) => r.data);

export const adminRevokeSanction = (sanctionId: string, reason: string, totpCode: string) =>
  API.post<AdminSanctionDto>(`${ADMIN_SANCTION_BASE}/${sanctionId}/revoke`, { reason }, { params: { totpCode } }).then(
    (r) => r.data
  );

export const adminListBlacklist = () => API.get<BlacklistEntry[]>(ADMIN_BLACKLIST_BASE).then((r) => r.data);

export const adminRemoveBlacklist = (entryId: string, reason: string, totpCode: string) =>
  API.post<void>(`${ADMIN_BLACKLIST_BASE}/${entryId}/remove`, { reason }, { params: { totpCode } }).then(() => undefined);
